import React, { useRef, useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import html2canvas from "html2canvas";
import { T } from "../theme";

/**
 * StoryShare — gera uma arte 1080×1920 (formato Stories) da vaga e permite
 * baixar em PNG ou compartilhar (Web Share API). A arte é renderizada fora da
 * tela em tamanho real e capturada com html2canvas.
 *
 * O tema é escolhível no modal (claro/escuro) e a cor de destaque vem da
 * empresa dona da vaga (`co.color`); o conteúdo vem dos dados da vaga.
 */

const FONT_HREF =
  "https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,400;12..96,600;12..96,700;12..96,800&family=Inter:wght@400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500;600;700&display=swap";

/** Injeta as fontes da arte (uma vez) e espera elas carregarem. */
async function ensureFonts() {
  if (!document.getElementById("story-fonts")) {
    const l = document.createElement("link");
    l.id = "story-fonts";
    l.rel = "stylesheet";
    l.href = FONT_HREF;
    document.head.appendChild(l);
  }
  try {
    await Promise.all([
      document.fonts.load('800 150px "Bricolage Grotesque"'),
      document.fonts.load('700 30px "Inter"'),
      document.fonts.load('400 30px "Inter"'),
      document.fonts.load('600 22px "JetBrains Mono"'),
    ]);
    await document.fonts.ready;
  } catch {
    /* fontes são best-effort — segue com fallback */
  }
}

/** Pré-carrega uma imagem (best-effort) pra garantir que o html2canvas a capture. */
function preloadImg(src) {
  return new Promise((res) => {
    if (!src) return res();
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => res();
    img.onerror = () => res();
    img.src = src;
  });
}

/** Título curto: tira "— Empresa" e "(...)" pra caber na arte. */
function shortTitle(title = "") {
  return title.split("—")[0].split("–")[0].split("(")[0].trim() || title;
}

/** Trunca texto longo pra não estourar o layout da arte. */
function clamp(text = "", max = 165) {
  const t = String(text).replace(/\s+/g, " ").trim();
  return t.length > max ? t.slice(0, max - 1).trimEnd() + "…" : t;
}

/* ══════════ BOTÃO (sidebar) ══════════ */
const TriggerBtn = styled.button`
  all: unset;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  box-sizing: border-box;
  padding: 11px;
  border: 1px solid ${T.border};
  border-radius: 10px;
  font-size: 0.83rem;
  font-weight: 600;
  color: ${T.muted};
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s, background 0.12s;
  &:hover {
    border-color: var(--purple);
    color: var(--purple);
    background: var(--purple-soft);
  }
`;

/* ══════════ MODAL ══════════ */
const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.72);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: sfIn 0.18s ease both;
  @keyframes sfIn { from { opacity: 0; } to { opacity: 1; } }
`;

const Modal = styled.div`
  background: ${T.card};
  border: 1px solid ${T.border};
  border-radius: 20px;
  padding: 22px;
  width: 100%;
  max-width: min(94vw, 680px);
  max-height: 92vh;
  overflow-y: auto;
  box-shadow: 0 30px 80px rgba(0, 0, 0, 0.55);
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const Body = styled.div`
  display: flex;
  gap: 22px;
  align-items: stretch;
  @media (max-width: 600px) {
    flex-direction: column;
    align-items: center;
  }
`;

const Controls = styled.div`
  flex: 1;
  min-width: 220px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 14px;
  @media (max-width: 600px) {
    width: 100%;
  }
`;

const ModalHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ModalTitle = styled.p`
  font-size: 0.95rem;
  font-weight: 800;
  color: ${T.text};
  letter-spacing: -0.01em;
`;

const CloseBtn = styled.button`
  all: unset;
  cursor: pointer;
  color: ${T.muted2};
  font-size: 1.2rem;
  line-height: 1;
  padding: 4px;
  border-radius: 8px;
  &:hover { color: ${T.text}; }
`;

/* seletor de tema claro/escuro */
const Segmented = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  border-radius: 12px;
  background: ${T.card2};
  border: 1px solid ${T.border};
`;

const Seg = styled.button`
  all: unset;
  flex: 1;
  text-align: center;
  box-sizing: border-box;
  padding: 9px;
  border-radius: 9px;
  font-size: 0.8rem;
  font-weight: 700;
  cursor: pointer;
  color: ${(p) => (p.$active ? "#fff" : T.muted)};
  background: ${(p) => (p.$active ? "var(--purple)" : "transparent")};
  transition: background 0.12s, color 0.12s;
  &:hover { color: ${(p) => (p.$active ? "#fff" : T.text)}; }
  &:disabled { cursor: progress; }
`;

/* área da prévia (dimensionada pela altura pra sempre caber na tela) */
const PreviewBox = styled.div`
  position: relative;
  height: clamp(320px, 56vh, 520px);
  aspect-ratio: 1080 / 1920;
  flex-shrink: 0;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid ${T.border};
  background: ${(p) => (p.$light ? "#F1EAFB" : "#0b0a14")};
  display: flex;
  align-items: center;
  justify-content: center;
  @media (max-width: 600px) {
    height: clamp(340px, 58vh, 470px);
  }
`;

const Preview = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const Spinner = styled.div`
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 3px solid rgba(155, 81, 255, 0.25);
  border-top-color: var(--purple);
  animation: sfSpin 0.7s linear infinite;
  @keyframes sfSpin { to { transform: rotate(360deg); } }
`;

const Actions = styled.div`
  display: flex;
  gap: 10px;
`;

const PrimaryBtn = styled.button`
  all: unset;
  flex: 1;
  text-align: center;
  box-sizing: border-box;
  padding: 13px;
  border-radius: 11px;
  background: var(--purple);
  color: #fff;
  font-weight: 800;
  font-size: 0.9rem;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(155, 81, 255, 0.3);
  transition: filter 0.12s, transform 0.1s;
  &:hover { filter: brightness(1.1); transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: progress; box-shadow: none; transform: none; }
`;

const GhostBtn = styled.button`
  all: unset;
  flex: 1;
  text-align: center;
  box-sizing: border-box;
  padding: 13px;
  border-radius: 11px;
  border: 1px solid ${T.border};
  color: ${T.muted};
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: border-color 0.12s, color 0.12s;
  &:hover { border-color: var(--purple); color: var(--purple); }
  &:disabled { opacity: 0.5; cursor: progress; }
`;

const Hint = styled.p`
  font-size: 0.72rem;
  color: ${T.muted2};
  text-align: center;
  line-height: 1.5;
`;

/* Container fora da tela onde a arte é renderizada em tamanho real (1080×1920). */
const OffScreen = styled.div`
  position: fixed;
  left: -99999px;
  top: 0;
  pointer-events: none;
`;

/* ══════════ TEMAS DA ARTE ══════════ */
function artTheme(variant, accent) {
  if (variant === "light") {
    return {
      bg: "linear-gradient(165deg, #FBF9FF 0%, #F1EAFB 55%, #E9DEF7 100%)",
      fallback: "#F1EAFB",
      text: "#0B0420",
      muted: "#3A2A52",
      muted2: "#6B5B82",
      line: "rgba(66,21,101,0.16)",
      grid: "rgba(66,21,101,0.03)",
      glow1: `${accent}22`,
      glow2: `${accent}14`,
    };
  }
  return {
    bg: "#0B0A14",
    fallback: "#0B0A14",
    text: "#F4F1FA",
    muted: "rgba(244,241,250,0.62)",
    muted2: "rgba(244,241,250,0.42)",
    line: "rgba(244,241,250,0.14)",
    grid: "rgba(244,241,250,0.022)",
    glow1: `${accent}26`,
    glow2: `${accent}18`,
  };
}

/* ══════════ ARTE 1080×1920 ══════════ */
function StoryArt({ artRef, job, co, variant }) {
  const accent = co?.color || "#8E4DFF";
  const th = artTheme(variant, accent);

  const st = shortTitle(job.title);
  const L = st.length;
  const titleSize = L <= 12 ? 150 : L <= 20 ? 122 : L <= 30 ? 96 : L <= 44 ? 74 : 60;

  const terms = [];
  if (job.faixa) terms.push(["Remuneração", job.faixa]);
  if (job.nivel) terms.push(["Nível", job.nivel]);
  if (job.regime) terms.push(["Regime", job.regime]);
  terms.push(["Local", job.local || "100% Remoto"]);
  const rows = terms.slice(0, 4);

  const mono = "'JetBrains Mono', monospace";
  const bric = "'Bricolage Grotesque', sans-serif";

  return (
    <OffScreen aria-hidden="true">
      <div
        ref={artRef}
        style={{
          width: 1080,
          height: 1920,
          background: th.bg,
          position: "relative",
          overflow: "hidden",
          fontFamily: "Inter, sans-serif",
          color: th.text,
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* glows */}
        <div style={{ position: "absolute", top: -260, right: -180, width: 760, height: 760, borderRadius: "50%", background: `radial-gradient(circle, ${th.glow1} 0%, transparent 62%)` }} />
        <div style={{ position: "absolute", bottom: -220, left: -180, width: 620, height: 620, borderRadius: "50%", background: `radial-gradient(circle, ${th.glow2} 0%, transparent 65%)` }} />
        {/* grid */}
        <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(${th.grid} 1px, transparent 1px), linear-gradient(90deg, ${th.grid} 1px, transparent 1px)`, backgroundSize: "90px 90px" }} />

        {/* topo */}
        <div style={{ position: "relative", padding: "80px 80px 0", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 22 }}>
            {co?.logo ? (
              <div style={{ width: 96, height: 96, borderRadius: 22, background: "#fff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, boxShadow: `0 10px 34px ${accent}44` }}>
                <img src={co.logo} crossOrigin="anonymous" alt="" style={{ width: 66, height: 66, objectFit: "contain" }} />
              </div>
            ) : (
              <div style={{ width: 96, height: 96, borderRadius: 22, background: accent, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontFamily: bric, fontWeight: 800, fontSize: 38, flexShrink: 0, boxShadow: `0 10px 34px ${accent}44` }}>
                {co?.initials || "G"}
              </div>
            )}
            <div>
              <div style={{ fontFamily: bric, fontWeight: 800, fontSize: 44, letterSpacing: "-0.02em", color: th.text, lineHeight: 1 }}>{co?.name || "Guebly"}</div>
              <div style={{ fontFamily: mono, fontSize: 18, color: th.muted2, letterSpacing: "0.05em", marginTop: 8 }}>{co?.segment || "Guebly Holding"}</div>
            </div>
          </div>
          <div style={{ textAlign: "right", fontFamily: mono, fontSize: 19, color: th.muted2, lineHeight: 1.7, letterSpacing: "0.05em" }}>
            {(job.local || "Remoto").toUpperCase()}
            <br />
            {(job.regime || "").toUpperCase()}
          </div>
        </div>

        {/* eyebrow */}
        <div style={{ position: "relative", padding: "72px 80px 0", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
            <div style={{ width: 80, height: 3, background: accent }} />
            <span style={{ fontFamily: mono, fontSize: 24, color: accent, letterSpacing: "0.22em", textTransform: "uppercase" }}>Vaga aberta</span>
          </div>
        </div>

        {/* título + resumo */}
        <div style={{ position: "relative", padding: "34px 80px 0", flexShrink: 0 }}>
          <h1 style={{ fontFamily: bric, fontSize: titleSize, lineHeight: 0.9, letterSpacing: "-0.04em", fontWeight: 800, margin: 0, color: th.text }}>{st}</h1>
          {job.resumo && (
            <p style={{ fontSize: 34, lineHeight: 1.4, color: th.muted, fontWeight: 400, marginTop: 36, maxWidth: 850 }}>{clamp(job.resumo, 175)}</p>
          )}
        </div>

        <div style={{ flex: 1 }} />

        {/* termos */}
        <div style={{ position: "relative", padding: "0 80px", flexShrink: 0 }}>
          {rows.map(([label, value], i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "280px 1fr",
                gap: 30,
                padding: "28px 0",
                borderTop: `1px solid ${th.line}`,
                borderBottom: i === rows.length - 1 ? `1px solid ${th.line}` : "none",
                alignItems: "baseline",
              }}
            >
              <div style={{ fontFamily: mono, fontSize: 22, color: th.muted2, letterSpacing: "0.08em", textTransform: "uppercase" }}>{label}</div>
              <div style={{ fontSize: 38, fontWeight: 700, color: th.text, letterSpacing: "-0.02em", lineHeight: 1.12 }}>{clamp(value, 46)}</div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{ position: "relative", padding: "56px 80px 80px", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
            <div>
              <div style={{ fontFamily: mono, fontSize: 20, color: th.muted2, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 12 }}>Candidate-se em</div>
              <div style={{ fontFamily: bric, fontSize: 46, fontWeight: 800, color: accent, letterSpacing: "-0.01em" }}>vagas.guebly.com.br</div>
              <div style={{ fontSize: 23, color: th.muted2, marginTop: 10 }}>@gueblybr</div>
            </div>
            <div style={{ width: 108, height: 108, borderRadius: 54, border: `2px solid ${accent}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={accent} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M13 6l6 6-6 6" />
              </svg>
            </div>
          </div>
        </div>
      </div>
    </OffScreen>
  );
}

/* ══════════ COMPONENTE PRINCIPAL ══════════ */
export default function StoryShare({ job, co }) {
  const artRef = useRef(null);
  const [variant, setVariant] = useState("dark");
  const [busy, setBusy] = useState(false);
  const [pngUrl, setPngUrl] = useState("");
  const [open, setOpen] = useState(false);
  const canShareFiles = typeof navigator !== "undefined" && !!navigator.canShare;

  /** Captura a arte (no tema atual) para PNG. */
  const capture = useCallback(async () => {
    if (!artRef.current) return;
    setBusy(true);
    try {
      await Promise.all([ensureFonts(), preloadImg(co?.logo)]);
      // dois frames pra garantir layout + fontes aplicadas
      await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
      const canvas = await html2canvas(artRef.current, {
        width: 1080,
        height: 1920,
        scale: 1,
        backgroundColor: variant === "light" ? "#F1EAFB" : "#0B0A14",
        useCORS: true,
        logging: false,
      });
      setPngUrl(canvas.toDataURL("image/png"));
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("Falha ao gerar a arte da vaga:", e);
    } finally {
      setBusy(false);
    }
  }, [variant, co]);

  // (re)gera quando o modal abre ou o tema muda
  useEffect(() => {
    if (!open) return;
    capture();
  }, [open, capture]);

  const download = useCallback(() => {
    if (!pngUrl) return;
    const a = document.createElement("a");
    a.href = pngUrl;
    a.download = `vaga-${job.slug || "guebly"}-story-${variant}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
  }, [pngUrl, job.slug, variant]);

  const share = useCallback(async () => {
    if (!pngUrl) return;
    try {
      const blob = await (await fetch(pngUrl)).blob();
      const file = new File([blob], `vaga-${job.slug || "guebly"}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: job.title,
          text: `Vaga aberta: ${shortTitle(job.title)} — candidate-se em vagas.guebly.com.br`,
        });
      } else {
        download();
      }
    } catch {
      /* usuário cancelou o compartilhamento */
    }
  }, [pngUrl, job.slug, job.title, download]);

  // fechar com Esc
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <>
      <TriggerBtn onClick={() => setOpen(true)}>
        <span aria-hidden="true">📲</span> Arte para stories
      </TriggerBtn>

      {/* arte renderizada fora da tela (capturada pelo html2canvas) */}
      <StoryArt artRef={artRef} job={job} co={co} variant={variant} />

      {open && (
        <Overlay onClick={() => setOpen(false)} role="dialog" aria-modal="true" aria-label="Arte da vaga para stories">
          <Modal onClick={(e) => e.stopPropagation()}>
            <ModalHead>
              <ModalTitle>Arte para stories</ModalTitle>
              <CloseBtn onClick={() => setOpen(false)} aria-label="Fechar">×</CloseBtn>
            </ModalHead>

            <Body>
              <PreviewBox $light={variant === "light"}>
                {busy || !pngUrl ? (
                  <Spinner aria-label="Gerando arte" />
                ) : (
                  <Preview src={pngUrl} alt="Prévia da arte da vaga (1080×1920)" />
                )}
              </PreviewBox>

              <Controls>
                <Segmented role="group" aria-label="Tema da arte">
                  <Seg $active={variant === "dark"} disabled={busy} onClick={() => setVariant("dark")} aria-pressed={variant === "dark"}>
                    <span aria-hidden="true">🌙</span> Escuro
                  </Seg>
                  <Seg $active={variant === "light"} disabled={busy} onClick={() => setVariant("light")} aria-pressed={variant === "light"}>
                    <span aria-hidden="true">☀️</span> Claro
                  </Seg>
                </Segmented>

                <Actions>
                  {canShareFiles ? (
                    <>
                      <PrimaryBtn onClick={share} disabled={busy || !pngUrl}>Compartilhar</PrimaryBtn>
                      <GhostBtn onClick={download} disabled={busy || !pngUrl}>Baixar PNG</GhostBtn>
                    </>
                  ) : (
                    <PrimaryBtn onClick={download} disabled={busy || !pngUrl}>Baixar PNG</PrimaryBtn>
                  )}
                </Actions>

                <Hint>1080×1920 · ideal pra Stories do Instagram. No celular, use "Compartilhar" pra postar direto.</Hint>
              </Controls>
            </Body>
          </Modal>
        </Overlay>
      )}
    </>
  );
}
